import {apiFetch} from "../client";

// Telegram recipients domain (admin)

// chatId stays a string end to end: Telegram ids are large enough that turning
// them into a JS number is a needless precision risk. The backend accepts both
// forms and parses once. Only personal ids are valid — a negative id belongs to
// a group or a channel and is refused by the service and by a database CHECK.
export interface TelegramRecipientItem {
    id: string;
    chatId: string;
    title: string;
    enabled: boolean;
    comment: string | null;
    lastOkAt: string | null;
    lastError: string | null;
    lastErrorAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export type TelegramBotKey = "payments" | "payments_admin";

export interface TelegramRecipientsResponse {
    items: TelegramRecipientItem[];
    // Bot API host in use; empty means nothing can be sent. A stand may point it
    // at a private fake, and "delivered" there is not "delivered to Telegram".
    botEndpoint: string;
    // The kill switch alone. Enabled with an empty endpoint is the "no token"
    // state — a different fix from a switched-off bot.
    botEnabled: boolean;
    botKey: TelegramBotKey;
    notifyOrders: boolean;
    notifyAdminNotifications: boolean;
}

export interface TelegramRecipientSettings {
    botKey: TelegramBotKey;
    notifyOrders: boolean;
    notifyAdminNotifications: boolean;
}

export interface TelegramRecipientRequest {
    chatId: string;
    title: string;
    enabled: boolean;
    comment?: string | null;
}

export interface TelegramRecipientIDResponse {
    id: string;
}

export interface TelegramRecipientTestResponse {
    ok: boolean;
    error?: string;
    botEndpoint: string;
    botEnabled: boolean;
}

function botQuery(bot: TelegramBotKey = "payments"): string {
    return `?bot=${encodeURIComponent(bot)}`;
}

export function fetchTelegramRecipients(token: string | null, bot: TelegramBotKey = "payments"): Promise<TelegramRecipientsResponse> {
    return apiFetch<TelegramRecipientsResponse>(`/telegram-recipients${botQuery(bot)}`, token);
}

export function createTelegramRecipient(
    token: string | null,
    body: TelegramRecipientRequest,
    bot: TelegramBotKey = "payments",
): Promise<TelegramRecipientIDResponse> {
    return apiFetch<TelegramRecipientIDResponse>(`/telegram-recipients${botQuery(bot)}`, token, {
        method: "POST",
        body: JSON.stringify(body),
    });
}

export function updateTelegramRecipient(
    token: string | null,
    id: string,
    body: TelegramRecipientRequest,
    bot: TelegramBotKey = "payments",
): Promise<TelegramRecipientIDResponse> {
    return apiFetch<TelegramRecipientIDResponse>(`/telegram-recipients/${encodeURIComponent(id)}${botQuery(bot)}`, token, {
        method: "PATCH",
        body: JSON.stringify(body),
    });
}

export function deleteTelegramRecipient(token: string | null, id: string, bot: TelegramBotKey = "payments"): Promise<void> {
    return apiFetch<void>(`/telegram-recipients/${encodeURIComponent(id)}${botQuery(bot)}`, token, {
        method: "DELETE",
    });
}

// A failed delivery answers 200 with ok=false: it describes the recipient, not
// a broken request, so it must not surface as a thrown API error.
export function testTelegramRecipient(
    token: string | null,
    id: string,
    bot: TelegramBotKey = "payments",
): Promise<TelegramRecipientTestResponse> {
    return apiFetch<TelegramRecipientTestResponse>(`/telegram-recipients/${encodeURIComponent(id)}/test${botQuery(bot)}`, token, {
        method: "POST",
    });
}

export function updateTelegramRecipientSettings(
    token: string | null,
    settings: Pick<TelegramRecipientSettings, "notifyOrders" | "notifyAdminNotifications">,
    bot: TelegramBotKey = "payments",
): Promise<TelegramRecipientSettings> {
    return apiFetch<TelegramRecipientSettings>(`/telegram-recipients/settings${botQuery(bot)}`, token, {
        method: "PATCH",
        body: JSON.stringify(settings),
    });
}
